/**
 * The ChatGPT handoff page, lite.nanome.ai/open?s=<token> (plan addendum C3). The MCP Worker's open_in_nanome and
 * request_advanced_action tools store {entries, components} under a random token for 24 h; this page fetches it
 * from GET {MCP_ORIGIN}/session/<token>, rebuilds the scene and opens the existing Open in Nanome dialog at its
 * pre-flight step. The token is also kept in sessionStorage so a full-page OAuth redirect (popup blocked) can come
 * back to '/' and rebuild the same scene before the dialog reopens.
 */
import type { LiteStore } from '@nanome-lite/state';
import { asComponents, asEntries, MCP_ORIGIN, restoreScene } from './restore';

export const HANDOFF_PATH = '/open';
export const HANDOFF_KEY = 'nanome-lite.handoff';
const TOKEN = /^[A-Za-z0-9_-]{16,64}$/;

export function handoffTokenFromUrl(href = location.href): string | undefined {
  const u = new URL(href);
  if (u.pathname.replace(/\/+$/, '') !== HANDOFF_PATH) return undefined;
  const s = u.searchParams.get('s');
  return s && TOKEN.test(s) ? s : undefined;
}

export class HandoffError extends Error { override readonly name = 'HandoffError'; }

export async function loadHandoff(store: LiteStore, token: string, fetchImpl: typeof fetch = fetch): Promise<{ entries: number; components: number }> {
  let res: Response;
  try { res = await fetchImpl(`${MCP_ORIGIN}/session/${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } }); }
  catch { throw new HandoffError('Could not reach Nanome Lite to fetch this structure. Check the connection and reload.'); }
  if (res.status === 404) throw new HandoffError('This link has expired (links work for 24 hours). Ask ChatGPT for a new one.');
  if (!res.ok) throw new HandoffError(`Could not fetch this structure (HTTP ${res.status}).`);
  const body = await res.json() as { entries?: unknown; components?: unknown };
  const entries = asEntries(body.entries);
  if (!entries) throw new HandoffError('This link has no structure in it.');
  const components = asComponents(body.components);
  const map = await restoreScene(store, entries, components);
  if (!map.size) throw new HandoffError('The structure could not be loaded.');
  return { entries: map.size, components: store.components.length };
}
